type EventLike = {
  date?: string | null
  start_datetime?: string | null
  end_datetime?: string | null
}

const EVENT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
}

const FALLBACK_LABEL = "Date to be announced"

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const getEventDate = (event: EventLike): Date | null => {
  return parseDate(event.start_datetime) ?? parseDate(event.date) ?? parseDate(event.end_datetime)
}

export const withTimeZone = (
  options: Intl.DateTimeFormatOptions,
  timezone?: string | null
): Intl.DateTimeFormatOptions => {
  if (!timezone) return options
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone })
    return { ...options, timeZone: timezone }
  } catch {
    return options
  }
}

// #1399 — a sale-window timestamp (ticket tier sales open/close), rendered
// in the event's own timezone the same way formatEventDate is. Lives here
// (not inline in the hosting page) so it can be unit tested without
// importing a Server Component's module chain (~/lib/supabase/server, ~/env).
export const formatSaleDate = (
  dateStr: string | null | undefined,
  timezone?: string | null
): string => {
  if (!dateStr) return "—"
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return "—"
  try {
    const dateLabel = date.toLocaleDateString(
      "en-GB",
      withTimeZone({ day: "numeric", month: "short", year: "numeric" }, timezone)
    )
    const timeLabel = date.toLocaleTimeString(
      "en-GB",
      withTimeZone({ hour: "2-digit", minute: "2-digit", hour12: false }, timezone)
    )
    return `${dateLabel}, ${timeLabel}`
  } catch {
    return "—"
  }
}

export const formatEventDate = (
  event: EventLike,
  locale = "en-US",
  options: Intl.DateTimeFormatOptions = EVENT_DATE_OPTIONS,
  timezone?: string | null
): string => {
  const date = getEventDate(event)
  if (!date) return FALLBACK_LABEL
  if (timezone) {
    try {
      return date.toLocaleDateString(locale, {
        ...options,
        timeZone: timezone,
        timeZoneName: "short",
      })
    } catch (error) {
      console.error("Failed to format event date in event timezone:", error)
    }
  }
  try {
    return date.toLocaleDateString(locale, options)
  } catch (error) {
    console.error("Failed to format event date:", error)
    return FALLBACK_LABEL
  }
}


